import type { MatrixClient } from 'matrix-js-sdk';
import { apiFetch } from './api/http.js';
import { nativeBridge } from './secretarioNative.js';

/**
 * Texto da mensagem no aviso de quem recebe, sem o servidor conseguir ler.
 * Antes de enviar, o app cifra um trecho curto para a chave pública de cada
 * aparelho do destinatário que ligou a opção e deixa no servidor por alguns
 * segundos; o trecho segue junto com o aviso e só aquele aparelho abre.
 */
type PreviewKey = { keyId: string; username: string; publicKey: string };

const KEY_TTL_MS = 5 * 60 * 1000;
const MAX_MEMBERS = 12;
const BUDGET_MS = 1500;
const cache = new Map<string, { at: number; keys: PreviewKey[] }>();

const localpartOf = (userId: string) => userId.slice(1).split(':')[0] ?? '';

function bytesOf(base64: string): Uint8Array<ArrayBuffer> {
  const binary = atob(base64);
  const bytes = new Uint8Array(new ArrayBuffer(binary.length));
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return bytes;
}

function base64Of(buffer: ArrayBuffer | Uint8Array): string {
  const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

async function keysFor(usernames: string[]): Promise<PreviewKey[]> {
  const now = Date.now();
  const missing = usernames.filter((name) => {
    const hit = cache.get(name);
    return !hit || now - hit.at > KEY_TTL_MS;
  });
  if (missing.length > 0) {
    const { keys } = await apiFetch<{ keys: PreviewKey[] }>('/v1/push/preview-keys', {
      method: 'POST',
      body: { usernames: missing.slice(0, 20) },
    });
    for (const name of missing) {
      cache.set(name, { at: now, keys: keys.filter((key) => key.username === name) });
    }
  }
  return usernames.flatMap((name) => cache.get(name)?.keys ?? []);
}

/** "chave.conteúdo": a chave AES e o vetor vão cifrados para o aparelho (RSA-OAEP). */
async function seal(publicKey: string, plain: Uint8Array<ArrayBuffer>): Promise<string> {
  const rsa = await crypto.subtle.importKey(
    'spki',
    bytesOf(publicKey),
    { name: 'RSA-OAEP', hash: 'SHA-1' },
    false,
    ['encrypt'],
  );
  const head = crypto.getRandomValues(new Uint8Array(new ArrayBuffer(44)));
  const aes = await crypto.subtle.importKey('raw', head.slice(0, 32), 'AES-GCM', false, [
    'encrypt',
  ]);
  const body = await crypto.subtle.encrypt({ name: 'AES-GCM', iv: head.slice(32) }, aes, plain);
  const sealedHead = await crypto.subtle.encrypt({ name: 'RSA-OAEP' }, rsa, head);
  return `${base64Of(sealedHead)}.${base64Of(body)}`;
}

async function announce(client: MatrixClient, roomId: string, text: string): Promise<void> {
  const room = client.getRoom(roomId);
  const me = client.getUserId();
  if (!room || !me) return;
  const others = room
    .getJoinedMembers()
    .map((member) => member.userId)
    .filter((userId) => userId !== me);
  if (others.length === 0 || others.length > MAX_MEMBERS) return;
  const keys = await keysFor(others.map(localpartOf));
  if (keys.length === 0) return;
  const sender = room.getMember(me)?.name || client.getUser(me)?.displayName || localpartOf(me);
  const plain = new TextEncoder().encode(
    JSON.stringify({ t: text.replace(/\s+/g, ' ').trim().slice(0, 180), n: sender.slice(0, 60) }),
  ) as Uint8Array<ArrayBuffer>;
  const items = await Promise.all(
    keys.map(async (key) => ({ keyId: key.keyId, ciphertext: await seal(key.publicKey, plain) })),
  );
  await apiFetch<void>('/v1/push/previews', { method: 'POST', body: { roomId, items } });
}

/**
 * Chamado logo antes de enviar uma mensagem. Nunca atrasa o envio além de um
 * instante e nunca o impede: sem o trecho, o aviso sai só com "Nova mensagem".
 */
export async function announcePreview(
  client: MatrixClient,
  roomId: string,
  text: string,
): Promise<void> {
  if (!text.trim()) return;
  await Promise.race([
    announce(client, roomId, text).catch(() => {}),
    new Promise<void>((resolve) => setTimeout(resolve, BUDGET_MS)),
  ]);
}

/** Este aparelho passa a receber o texto no aviso (chave publicada no servidor). */
export async function enablePreview(pushToken: string): Promise<boolean> {
  const publicKey = await nativeBridge.previewKey();
  if (!publicKey) return false;
  try {
    await apiFetch<unknown>('/v1/push/preview-key', {
      method: 'PUT',
      body: { pushkey: pushToken, publicKey },
    });
    return true;
  } catch {
    return false;
  }
}

export async function disablePreview(pushToken: string | null): Promise<void> {
  if (pushToken) {
    await apiFetch<void>('/v1/push/preview-key', {
      method: 'DELETE',
      body: { pushkey: pushToken },
    }).catch(() => {});
  }
  await nativeBridge.removePreviewKey();
}

const WANTED_KEY = 'secretario.aviso.texto';

/** A pessoa ligou "mostrar o texto no aviso" neste aparelho? */
export function previewWanted(): boolean {
  try {
    return window.localStorage.getItem(WANTED_KEY) === '1';
  } catch {
    return false;
  }
}

export function setPreviewWanted(wanted: boolean): void {
  try {
    if (wanted) window.localStorage.setItem(WANTED_KEY, '1');
    else window.localStorage.removeItem(WANTED_KEY);
  } catch {
    /* sem armazenamento: vale até fechar o app */
  }
}
